import { useCallback, useEffect, useState } from "react";
import { getField } from "../fields";
import type { FieldValues } from "../engine/types";
import { ApiError, fetchHistory, type HistoryItem } from "../engine/useRun";
import { useSession } from "./session";
import { ResultView } from "./ResultView";
import { Button, Card, Chip, EmptyState, ErrorState, Skeleton } from "./primitives";
import { Icon } from "./Icon";
import type { Route } from "./router";

/**
 * History: past runs, newest first, each re-openable AND re-runnable with
 * edited inputs. Edit-and-rerun is the headline affordance here, not a footnote
 * — it's the thing a chat box structurally cannot do.
 */

export function HistoryScreen({
  navigate,
  onRerun,
}: {
  navigate: (r: Route) => void;
  /** Hands the stored inputs back to the field screen as a prefill. */
  onRerun: (fieldId: string, values: FieldValues) => void;
}) {
  const { user, openAuth } = useSession();
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setItems(null);
    try {
      setItems(await fetchHistory());
    } catch (e) {
      if (e instanceof ApiError && e.code === "unauthorized") {
        setItems([]);
        return;
      }
      setError(e instanceof ApiError ? e : new ApiError("صار خطأ", 500, "server"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, user]);

  if (!user) {
    return (
      <EmptyState
        icon="history"
        title="تاريخك يحتاج حساب"
        body="سجّل دخول بالموبايل حتى ينحفظ كل تشغيل، وتكدر ترجع له وتعدّله وتعيده وقت ما تريد."
        action={
          <Button icon="login" onClick={() => openAuth()}>
            سجّل دخول
          </Button>
        }
      />
    );
  }

  if (error) return <ErrorState body={error.message} onRetry={() => void load()} />;

  if (items === null) {
    return (
      <div className="grid gap-3">
        <Skeleton className="h-8 w-40" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon="inbox"
        title="ما عندك تشغيلات بعد"
        body="أول ما تشغّل أداة، راح تلكاها هنا — وتكدر تعيدها بجواب معدّل."
        action={
          <Button icon="grid_view" onClick={() => navigate({ name: "catalog" })}>
            شوف الأدوات
          </Button>
        }
      />
    );
  }

  return (
    <div className="grid gap-4">
      <div>
        <h1 className="md-headline-medium text-on-surface">تاريخي</h1>
        <p className="md-body-medium mt-1 text-on-surface-variant">
          كل تشغيل محفوظ بأجوبته. افتحه، بدّل جواب واحد، وأعِد التشغيل.
        </p>
      </div>

      <div className="grid gap-3">
        {items.map((item) => (
          <HistoryCard
            key={item.id}
            item={item}
            open={openId === item.id}
            onToggle={() => setOpenId(openId === item.id ? null : item.id)}
            onRerun={onRerun}
          />
        ))}
      </div>
    </div>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("ar-IQ", {
    dateStyle: "medium",
    timeStyle: "short",
    numberingSystem: "latn",
  }).format(d);
}

function HistoryCard({
  item,
  open,
  onToggle,
  onRerun,
}: {
  item: HistoryItem;
  open: boolean;
  onToggle: () => void;
  onRerun: (fieldId: string, values: FieldValues) => void;
}) {
  const pack = getField(item.fieldId);
  const title = pack?.title ?? item.fieldTitle ?? item.fieldId;
  // A one-line taste of the run, drawn from the pack's own declared source input
  // where there is one — never a hardcoded field name.
  const previewKey = pack?.output.sourceInput ?? pack?.inputs.find((i) => i.type === "textarea")?.name;
  const preview =
    previewKey && item.values ? String(item.values[previewKey] ?? "").slice(0, 140) : "";

  return (
    <Card variant="outlined" className="overflow-hidden">
      <div className="flex flex-wrap items-start gap-3 p-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary-container text-on-primary-container">
          <Icon name={pack?.icon ?? "bolt"} size={22} filled />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="md-title-medium text-on-surface">{title}</h3>
            {item.status === "refused" ? (
              <Chip tone="neutral" icon="info">
                بدون جواب
              </Chip>
            ) : null}
            {item.isDemo ? (
              <Chip tone="warn" icon="science">
                تجريبي
              </Chip>
            ) : null}
          </div>
          <p className="md-body-small mt-0.5 text-on-surface-variant">
            <bdi>{formatWhen(item.createdAt)}</bdi>
          </p>
          {preview ? (
            <p className="md-body-small mt-2 line-clamp-2 text-on-surface-variant">{preview}…</p>
          ) : null}
        </div>

        <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">
          {item.values && pack ? (
            <Button
              variant="filled"
              icon="replay"
              onClick={() => onRerun(item.fieldId, item.values as FieldValues)}
            >
              عدّل وأعِد
            </Button>
          ) : null}
          <Button variant="outlined" icon={open ? "expand_less" : "expand_more"} onClick={onToggle}>
            {open ? "إخفاء" : "افتح"}
          </Button>
        </div>
      </div>

      {open ? (
        <div className="border-t border-outline-variant bg-surface-low p-4">
          {pack ? (
            <ResultView pack={pack} result={item} />
          ) : (
            <p className="prose-ar md-body-medium text-on-surface">{item.output}</p>
          )}
        </div>
      ) : null}
    </Card>
  );
}
